import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Users, Plus, Search, Globe, Lock, Mail, UsersRound, Sparkles, ChevronRight, SlidersHorizontal } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { EmptyState } from "@/components/ui/empty-state";
import { TextMarquee } from "@/components/ui/text-marquee";
import { CommunityNavTabs } from "@/components/community/CommunityNavTabs";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { listCommunityFaces, listGroupsPublic, listMyGroups, type CommunityFace, type GroupRow } from "@/lib/groups.functions";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export const Route = createFileRoute("/comunidades/")({
  head: () => ({
    meta: [
      { title: "Comunidade — Viny Club" },
      { name: "description", content: "Espaços para conectar, compartilhar e se inspirar." },
      { property: "og:title", content: "Comunidade — Viny Club" },
      { property: "og:description", content: "Espaços para conectar, compartilhar e se inspirar." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: GruposIndex,
});

function PrivacyIcon({ p }: { p: GroupRow["privacy"] }) {
  if (p === "public") return <Globe className="h-3 w-3" />;
  if (p === "private") return <Lock className="h-3 w-3" />;
  return <Mail className="h-3 w-3" />;
}

function GroupAvatar({ g, size = "md" }: { g: GroupRow; size?: "sm" | "md" }) {
  const cls = size === "sm" ? "h-9 w-9 rounded-lg text-xs" : "h-12 w-12 rounded-xl text-base";
  return g.avatar_url ? (
    <img loading="lazy" decoding="async" src={g.avatar_url} alt="" className={`${cls} shrink-0 border border-[color:var(--hairline)] object-cover`} />
  ) : (
    <div className={`${cls} grid shrink-0 place-items-center border border-[color:var(--hairline)] bg-surface font-semibold text-muted-foreground`}>
      {g.name[0]?.toUpperCase()}
    </div>
  );
}

function GroupCard({ g }: { g: GroupRow }) {
  return (
    <Link
      to="/comunidades/$slug"
      params={{ slug: g.slug }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-surface/40 transition-all hover:border-primary/40 hover:bg-surface/80"
    >
      <div className="relative h-24 w-full overflow-hidden bg-gradient-to-br from-primary/20 via-surface to-surface">
        {g.banner_url && (
          <img loading="lazy" decoding="async" src={g.banner_url} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <div className="flex items-start gap-3">
          <div className="relative -mt-7 overflow-hidden rounded-xl border-2 border-background bg-surface">
            <GroupAvatar g={g} size="sm" />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[13px] font-semibold">{g.name}</span>
              {g.category?.startsWith("Oficial") && (
                <span className="shrink-0 rounded-full bg-foreground px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-background">Viny.club</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <PrivacyIcon p={g.privacy} />
              <span>{g.member_count} {g.member_count === 1 ? "membro" : "membros"}</span>
              {g.category && <span>· {g.category}</span>}
            </div>
          </div>
        </div>
        {g.description && (
          <p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">{g.description}</p>
        )}
      </div>
    </Link>
  );
}

const PRIVACY_FILTERS = [
  { id: "all", label: "Todas" },
  { id: "public", label: "Públicas" },
  { id: "private", label: "Privadas" },
] as const;

function FaceTile({ face }: { face: CommunityFace }) {
  return (
    <Link
      to="/profile/$username"
      params={{ username: face.username }}
      aria-label={`Ver perfil de ${face.display_name || face.username}`}
      className="group relative block h-20 w-20 overflow-hidden rounded-xl border border-foreground/10 bg-background/35 sm:h-24 sm:w-24"
    >
      <img
        src={face.avatar_url ?? undefined}
        alt={face.display_name || face.username}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover opacity-80 transition duration-300 group-hover:scale-105 group-hover:opacity-100"
      />
      <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-background/80 to-transparent px-2 pb-1.5 pt-5 text-[10px] font-medium text-foreground/90">
        {face.display_name || `@${face.username}`}
      </span>
    </Link>
  );
}

function PeopleMarquee({ faces }: { faces: CommunityFace[] }) {
  if (faces.length === 0) return null;
  // Com poucas pessoas o marquee repete as mesmas fotos; nesse caso mostra grade estática.
  if (faces.length < 10) {
    return (
      <div className="grid h-full grid-cols-3 content-center gap-3 px-8 py-7">
        {faces.slice(0, 9).map((face) => <FaceTile key={face.id} face={face} />)}
      </div>
    );
  }
  const rowCount = faces.length >= 24 ? 3 : 2;
  const rows = Array.from({ length: rowCount }, (_, rowIndex) => faces.filter((_, index) => index % rowCount === rowIndex));
  return (
    <div className="flex h-full flex-col justify-center gap-3 overflow-hidden py-7">
      {rows.map((row, index) => (
        <TextMarquee key={index} direction="horizontal" reverse={index === 1} speed={5}>
          {(row.length > 1 ? row : faces).map((face) => <FaceTile key={`${index}-${face.id}`} face={face} />)}
        </TextMarquee>
      ))}
    </div>
  );
}

function MobileCommunityRow({ g }: { g: GroupRow }) {
  return (
    <Link
      to="/comunidades/$slug"
      params={{ slug: g.slug }}
      className="grid grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-3 border-b border-foreground/10 py-4 active:bg-foreground/[0.03]"
    >
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-surface-elevated">
        {g.avatar_url || g.banner_url ? (
          <img src={g.avatar_url || g.banner_url || undefined} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
        ) : (
          <div className="grid h-full w-full place-items-center text-lg font-semibold text-muted-foreground">{g.name[0]?.toUpperCase()}</div>
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate font-display text-[15px] font-semibold">{g.name}</div>
        <p className="mt-0.5 line-clamp-1 text-[12.5px] text-muted-foreground">{g.description || g.category || "Comunidade de colecionadores"}</p>
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <PrivacyIcon p={g.privacy} />
          <span>{g.member_count} {g.member_count === 1 ? "membro" : "membros"}</span>
          {g.category && <><span>·</span><span className="truncate">{g.category}</span></>}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" />
    </Link>
  );
}

function MobileCommunities({
  groups,
  myGroups,
  q,
  onQueryChange,
  privacy,
  onPrivacyChange,
  isAuthenticated,
  isLoading,
}: {
  groups: GroupRow[];
  myGroups: Array<{ group: GroupRow }>;
  q: string;
  onQueryChange: (value: string) => void;
  privacy: (typeof PRIVACY_FILTERS)[number]["id"];
  onPrivacyChange: (value: (typeof PRIVACY_FILTERS)[number]["id"]) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}) {
  const [view, setView] = useState<"discover" | "mine">("discover");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const visible = view === "mine" ? myGroups.map((item) => item.group) : groups;

  return (
    <div className="min-h-[calc(100dvh-64px)] bg-background pb-28 lg:hidden">
      <CommunityNavTabs />
      <header className="border-b border-foreground/10 bg-background/90 px-4 pb-3 pt-1">

        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Viny Club</p>
            <h1 className="truncate font-display text-2xl font-semibold">Comunidades</h1>
          </div>
          <Button variant="secondary" size="icon" className="h-10 w-10 shrink-0 rounded-full" onClick={() => setFiltersOpen(true)} aria-label="Abrir filtros">
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-1 rounded-2xl border border-foreground/10 bg-surface/70 p-1">
          <Button variant="ghost" className={`h-9 rounded-xl text-xs ${view === "discover" ? "bg-foreground/10 text-foreground" : "text-muted-foreground"}`} onClick={() => setView("discover")}>Descobrir</Button>
          <Button variant="ghost" className={`h-9 rounded-xl text-xs ${view === "mine" ? "bg-foreground/10 text-foreground" : "text-muted-foreground"}`} onClick={() => setView("mine")}>Minhas</Button>
        </div>


        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(event) => onQueryChange(event.target.value)}
            type="search"
            inputMode="search"
            placeholder="Buscar comunidades"
            className="h-11 w-full rounded-xl border border-foreground/10 bg-surface pl-10 pr-4 text-base outline-none placeholder:text-muted-foreground focus:border-foreground/25"
          />
        </div>
      </header>

      <main className="px-4 pt-5">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            {view === "mine" ? "Suas comunidades" : privacy === "all" ? "Para descobrir" : privacy === "public" ? "Comunidades públicas" : "Comunidades privadas"}
          </h2>
          <span className="text-xs tabular-nums text-muted-foreground">{visible.length}</span>
        </div>

        {isLoading ? (
          <div className="divide-y divide-foreground/10">
            {Array.from({ length: 5 }).map((_, index) => <div key={index} className="h-24 animate-pulse bg-surface/30" />)}
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center">
            <UsersRound className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">{view === "mine" ? "Você ainda não participa de comunidades" : "Nenhuma comunidade encontrada"}</p>
            <p className="mx-auto mt-1 max-w-[260px] text-xs leading-relaxed text-muted-foreground">Ajuste a busca ou crie um espaço para reunir quem curte o mesmo som.</p>
          </div>
        ) : (
          <div>{visible.map((group) => <MobileCommunityRow key={group.id} g={group} />)}</div>
        )}
      </main>

      <Button asChild className="fixed bottom-[calc(env(safe-area-inset-bottom)+92px)] right-4 z-30 h-14 w-14 rounded-full p-0 shadow-[0_16px_40px_-12px_color-mix(in_oklab,var(--action)_55%,transparent)]">
        <Link to={isAuthenticated ? "/comunidades/novo" : "/auth"} aria-label="Criar comunidade"><Plus className="h-5 w-5" /></Link>
      </Button>

      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl border-foreground/10 px-4 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-3">
          <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-foreground/20" />
          <SheetHeader className="text-left"><SheetTitle>Filtrar comunidades</SheetTitle></SheetHeader>
          <div className="mt-5 grid gap-2">
            {PRIVACY_FILTERS.map((filter) => (
              <Button
                key={filter.id}
                variant={privacy === filter.id ? "default" : "secondary"}
                className="h-12 justify-start rounded-xl"
                onClick={() => { onPrivacyChange(filter.id); setFiltersOpen(false); setView("discover"); }}
              >
                {filter.label}
              </Button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CommunitiesHero({ faces }: { faces: CommunityFace[] }) {
  return (
    <div className="relative isolate overflow-hidden rounded-[var(--radius-2xl)] bg-surface-elevated">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(360px,44%)]">
        <div className="relative z-10 px-6 py-9 lg:px-10 lg:py-12">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground/55">Comunidade</p>
          <h1 className="mt-2 font-display text-3xl font-medium text-foreground lg:text-4xl">Descobrir comunidades</h1>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground/60 lg:text-[15px]">
            Entre, abra um tópico e converse sobre discos com quem curte o mesmo som.
          </p>
          <Link
            to="/comunidades/novo"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02]"
          >
            <Plus className="h-4 w-4" /> Criar comunidade
          </Link>
        </div>
        <div className="relative hidden min-h-64 lg:block">
          <PeopleMarquee faces={faces} />
          <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-surface-elevated via-surface-elevated/80 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-surface-elevated to-transparent" />
        </div>
      </div>
    </div>
  );
}

function GruposIndex() {
  const { isAuthenticated } = useCurrentUser();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [privacy, setPrivacy] = useState<(typeof PRIVACY_FILTERS)[number]["id"]>("all");

  const listPub = useServerFn(listGroupsPublic);
  const listMine = useServerFn(listMyGroups);
  const listFaces = useServerFn(listCommunityFaces);

  const groupsQ = useQuery({
    queryKey: ["groups-public", q, category],
    queryFn: () => listPub({ data: { q: q || undefined, category: category || undefined } }),
  });
  const mineQ = useQuery({
    queryKey: ["my-groups"],
    queryFn: () => listMine(),
    enabled: isAuthenticated,
  });
  const facesQ = useQuery({
    queryKey: ["community-faces"],
    queryFn: () => listFaces(),
    staleTime: 5 * 60_000,
  });

  const categories = useMemo(() => {
    const set = new Set<string>();
    (groupsQ.data ?? []).forEach((g) => g.category && set.add(g.category));
    return Array.from(set).sort();
  }, [groupsQ.data]);

  const filtered = useMemo(() => {
    const rows = groupsQ.data ?? [];
    if (privacy === "all") return rows;
    return rows.filter((g) => g.privacy === privacy);
  }, [groupsQ.data, privacy]);

  const myGroups = mineQ.data ?? [];

  return (
    <AppShell>
      <MobileCommunities
        groups={filtered}
        myGroups={myGroups}
        q={q}
        onQueryChange={setQ}
        privacy={privacy}
        onPrivacyChange={setPrivacy}
        isAuthenticated={isAuthenticated}
        isLoading={groupsQ.isLoading || (isAuthenticated && mineQ.isLoading)}
      />

      <div className="hidden lg:block">
        <CommunityNavTabs />
        <div className="mx-auto w-full max-w-[1500px] px-5 pt-10">
          <CommunitiesHero faces={facesQ.data ?? []} />
        </div>

      <div className="mx-auto w-full max-w-[1500px] px-5 py-8">
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Coluna principal — descoberta */}
          <div className="min-w-0 flex-1">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar comunidades…"
                  className="h-11 w-full rounded-full border border-[color:var(--hairline)] bg-surface/40 pl-11 pr-4 text-sm placeholder:text-muted-foreground/70 focus:border-foreground/30 focus:bg-surface/70 focus:outline-none"
                />
              </div>
              <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto">
                {PRIVACY_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setPrivacy(f.id)}
                    className={`h-11 shrink-0 rounded-full border px-4 text-[12.5px] font-medium transition-colors ${
                      privacy === f.id
                        ? "border-foreground bg-foreground text-background"
                        : "border-[color:var(--hairline)] bg-surface/40 text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {categories.length > 0 && (
              <div className="mb-6 flex flex-wrap items-center gap-1.5">
                <button
                  onClick={() => setCategory(null)}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                    !category
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-[color:var(--hairline)] text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                  }`}
                >
                  Tudo
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(category === c ? null : c)}
                    className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                      category === c
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-[color:var(--hairline)] text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {groupsQ.isLoading ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-44 animate-pulse rounded-2xl border border-[color:var(--hairline)] bg-surface/40" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                title="Nenhuma comunidade por aqui ainda"
                description="Que tal abrir o primeiro espaço? Reúna gente que curte o mesmo som que você."
                icons={[<UsersRound key="u" />, <Sparkles key="s" />, <Users key="g" />]}
                action={{ label: "Criar comunidade", href: "/comunidades/novo", icon: <Plus className="h-4 w-4" /> }}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((g) => <GroupCard key={g.id} g={g} />)}
              </div>
            )}
          </div>

          {/* Sidebar direita — suas comunidades */}
          {isAuthenticated && myGroups.length > 0 && (
            <aside className="w-full shrink-0 lg:w-64">
              <div className="rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-4 lg:sticky lg:top-24">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                    Suas comunidades
                  </h2>
                  <span className="rounded-full bg-surface px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground">
                    {myGroups.length}
                  </span>
                </div>
                <div className="flex flex-col">
                  {myGroups.map(({ group }) => (
                    <Link
                      key={group.id}
                      to="/comunidades/$slug"
                      params={{ slug: group.slug }}
                      className="group flex items-center gap-2.5 rounded-xl px-2 py-2 transition-colors hover:bg-surface"
                    >
                      <GroupAvatar g={group} size="sm" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-medium">{group.name}</div>
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <PrivacyIcon p={group.privacy} />
                          <span>{group.member_count} {group.member_count === 1 ? "membro" : "membros"}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>
      </div>
    </AppShell>
  );
}
